import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for M&A Content) ---
import {
    // Core Concepts & General
    faHandshake,        // Mergers / Acquisitions / Partnerships
    faExpandArrowsAlt,  // Expansion / Growth / Reach
    faBuilding,         // Facility / Organization
    faChartLine,        // Strategy / Financial Growth / Performance
    faTasks,            // Operational Efficiency / Integration
    faUsers,            // Staff / Culture / Patients
    faCheckCircle,      // Compliance / Success / Evaluation
    faBalanceScale,     // Legal / Regulatory
    faSearchDollar,     // Financial Due Diligence
    faExclamationTriangle, // Challenges / Risks
    // Cliniqor Support Steps
    faLightbulb,        // 1. Strategy Development
    faGavel,            // 2. Legal & Regulatory Compliance
    faCogs,             // 3. Operational Integration Planning
    faUserFriends,      // 4. Cultural & Staff Integration
    faBullhorn,         // 5. Branding, Marketing & PR
    faSearchPlus,       // 6. Post-Merger Evaluation & Monitoring
    // Benefits
    // faTasks reused for Op Efficiency
    faMapMarkedAlt,     // Market Penetration / Reach
    faSitemap,          // Service Diversification
    faShieldAlt,        // Risk Mitigation
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faCalculator, faMoneyCheckAlt, faFileInvoiceDollar, faExchangeAlt, faChartPie, faReceipt,
    faArrowsAltH, faUsersCog, faFileExcel, faTachometerAlt, faLandmark, faSearch,
    faFileSignature, faPlayCircle, faClipboardList, faBinoculars, faSyncAlt, faUserTie,
    faProjectDiagram, faMapSigns, faBullseye, faLaptopCode, faHandHoldingDollar, faTrophy,
    faThumbsUp, faMedal
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Understanding M&A / Reasons
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why M&A Important (Benefits)
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Challenges & Considerations
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // How Cliniqor Supports (Process)
    // Conclusion integrated into footer

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for M&A/strategy/collaboration
    const heroBgImage = 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image showing business handshake/deal

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Mergers & Acquisitions: Strategic Expansion</h1>
            <p className="cliniqor-sub-heading">
              Navigating Healthcare Facility Growth with Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              M&A offers impactful scaling through expanded capacity, broader services, and new markets. Cliniqor provides meticulous planning, due diligence, and integration strategies for complex M&A success.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- UNDERSTANDING M&A / REASONS --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Understanding Mergers & Acquisitions in Healthcare</h2>
              <p>Combining forces for accelerated growth and enhanced performance.</p>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>A **merger** unites two organizations into one, combining resources. An **acquisition** involves one purchasing another to absorb its operations. Both are valuable growth tools.</p>
                     <p>Hospitals pursue M&A to: utilize underused capacity (equipment, staff, facilities), increase revenue streams (patient volume, new markets), expand market reach (new geographic areas), and enhance clinical expertise/services (specialists, tech).</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- WHY M&A IMPORTANT (Benefits - Features Section) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Why Mergers & Acquisitions are Important for Healthcare Growth</h2>
              <p>Strategic options to scale quickly and overcome organic growth challenges.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained (4 items) */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div>
                    <h4>1. Operational Efficiency</h4>
                    <p>Streamlining operations, standardizing procedures, achieving economies of scale, reducing costs.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faMapMarkedAlt} /></div>
                    <h4>2. Market Penetration</h4>
                    <p>Rapidly entering new geographic markets, accessing broader populations, increasing patient volumes.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faSitemap} /></div>
                    <h4>3. Service Diversification</h4>
                    <p>Adding complementary specialties/services, creating one-stop destinations, building brand.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faShieldAlt} /></div>
                    <h4>4. Risk Mitigation</h4>
                    <p>Diversifying operations across different medical fields or regions, reducing market segment risks.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- CHALLENGES & CONSIDERATIONS (Features Section - Reused) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-gradient"> {/* Use gradient bg */}
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Challenges and Considerations in Mergers & Acquisitions</h2>
                 <p>Navigating potential hurdles for successful integration.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Challenges --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserFriends} /></div>
                            <h4>Cultural Integration</h4>
                            <p>Aligning values, communication, and working styles for harmonious collaboration.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBalanceScale} /></div>
                            <h4>Legal & Regulatory Compliance</h4>
                            <p>Adhering to government laws, healthcare regulations, and accreditation standards.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faSearchDollar} /></div>
                            <h4>Financial Due Diligence</h4>
                            <p>Rigorous evaluation of financial health, liabilities, and assets to avoid surprises.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faExclamationTriangle} /></div> {/* Operational Challenges */}
                            <h4>Operational Challenges</h4>
                            <p>Integrating systems, protocols, staffing, and IT requires significant planning.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div> {/* Staff Retention */}
                            <h4>Staff Transition & Retention</h4>
                            <p>Managing uncertainty and concern to maintain morale and retain top talent.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>


        {/* --- HOW CLINIQOR SUPPORTS (Detailed Planning Section - 6 steps) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>How Cliniqor Supports Your Mergers & Acquisitions Journey</h2>
               <p>Expert guidance through complexities for a smooth and efficient process.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
             <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginBottom: '30px' }}>
                 <p>With deep expertise in hospital management, legal regulations, and operational integration, we provide comprehensive advisory services for flawless execution.</p>
            </div>
            {/* Using Planning Items for the 6 Support Steps */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLightbulb} /></div>
                    <div className="cliniqor-content"><h4>1. Pre-M&A Strategy Development</h4> <p>Identifying partners, assessing financial viability, establishing objectives, market research, due diligence.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faGavel} /></div>
                    <div className="cliniqor-content"><h4>2. Legal and Regulatory Compliance</h4> <p>Managing legal due diligence, contract reviews, ensuring compliance with all relevant laws.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCogs} /></div>
                    <div className="cliniqor-content"><h4>3. Operational Integration Planning</h4> <p>Developing detailed plans to merge protocols, billing, staff roles, workflows, and IT systems.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUserFriends} /></div>
                    <div className="cliniqor-content"><h4>4. Cultural and Staff Integration</h4> <p>Guidance on HRM, training, change management to foster unity and retain key personnel.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBullhorn} /></div>
                    <div className="cliniqor-content"><h4>5. Branding, Marketing, and Public Relations</h4> <p>Rebranding strategies, PR to communicate the merger, marketing expanded capabilities.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSearchPlus} /></div>
                    <div className="cliniqor-content"><h4>6. Post-Merger Evaluation and Monitoring</h4> <p>Audits to assess integration success, identify gaps, provide ongoing support and optimization.</p> </div>
                 </div>
            </div>
          </div>
        </section>

       {/* Removed Conclusion section - integrated into Footer */}


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h2>Ensuring a Successful Merger or Acquisition</h2>
          <p>M&A offers powerful growth but requires careful planning and execution. With Cliniqor’s support, navigate complexities confidently for a smooth, profitable transition leading to increased patient access, better care, and long-term financial growth.</p>
          <a href="#contact" className="cliniqor-cta-button">Discuss Your M&A Strategy</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;